import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Steps,
  Button,
  Space,
  Alert,
  Form,
  InputNumber,
  Select,
  Checkbox,
  Divider,
  Progress,
  Typography,
  Row,
  Col,
  Card,
  Statistic,
  List,
  Tag,
  message,
  Spin
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  FilterOutlined,
  CheckOutlined,
  WarningOutlined,
  TeamOutlined,
  DollarOutlined
} from '@ant-design/icons';
import validationService from '../../../../services/validationService';
import { formatCurrency } from '../../utils';
import {
  BatchValidationModalProps,
  ValidationConstraint,
  ConditionalValidationCriteria,
  ConditionalValidationResult
} from '../../../../types/validation';

const { Step } = Steps;
const { Option } = Select;
const { Text, Title } = Typography;

const ModalValidationLot: React.FC<BatchValidationModalProps> = ({
  visible,
  selectedCalculations,
  onCancel,
  onValidate,
  onConditionalValidate,
  loading = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [validationType, setValidationType] = useState<'selected' | 'conditional'>('selected');
  const [customConstraints, setCustomConstraints] = useState<ValidationConstraint[]>([]);
  const [criteria, setCriteria] = useState<ConditionalValidationCriteria>({});
  const [validationResult, setValidationResult] = useState<ConditionalValidationResult | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [form] = Form.useForm();
  const requestVersion = useRef(0);
  const [constraintsReady, setConstraintsReady] = useState(false);
  const [constraintsError, setConstraintsError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setCustomConstraints([]); setConstraintsReady(false); setConstraintsError(null); setCriteria({});
      loadDefaultConstraints();
      setCurrentStep(0);
      setValidationResult(null);
    }
    return () => { requestVersion.current++; };
  }, [visible, selectedCalculations.join(',')]);

  const loadDefaultConstraints = async () => {
    const version = ++requestVersion.current;
    setConstraintsReady(false); setConstraintsError(null);
    try {
      const constraints = await validationService.getDefaultValidationConstraints();
      if (version !== requestVersion.current) return;
      if (!Array.isArray(constraints)) throw new Error('Les contraintes de validation reçues sont invalides');
      setCustomConstraints(constraints);
      setConstraintsReady(true);
    } catch (error) {
      if (version !== requestVersion.current) return;
      setConstraintsError(error instanceof Error ? error.message : 'Contraintes de validation indisponibles');
      console.error('Erreur lors du chargement des contraintes:', error);
    }
  };

  const handleNext = () => {
    setCurrentStep(currentStep + 1);
  };

  const handlePrev = () => {
    setCurrentStep(currentStep - 1);
  };

  const handleValidateSelected = async () => {
    if (!constraintsReady) return;
    try {
      setIsProcessing(true);
      await onValidate(selectedCalculations, customConstraints);
      message.success('Validation en lot réalisée avec succès');
      onCancel();
    } catch (error) {
      message.error('Erreur lors de la validation en lot');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConditionalValidation = async () => {
    if (!constraintsReady) return;
    try {
      setIsProcessing(true);
      const result = await onConditionalValidate(criteria, customConstraints);
      setValidationResult(result);
      handleNext();
    } catch (error) {
      message.error('Erreur lors de la validation conditionnelle');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFinishConditional = () => {
    message.success('Validation conditionnelle terminée');
    onCancel();
  };

  const addCustomConstraint = () => {
    const newConstraint: ValidationConstraint = {
      field: 'net_salary',
      operator: 'min',
      value: 0,
      message: 'Nouvelle contrainte'
    };
    setCustomConstraints([...customConstraints, newConstraint]);
  };

  const updateConstraint = (index: number, field: string, value: any) => {
    const updated = [...customConstraints];
    updated[index] = { ...updated[index], [field]: value };
    setCustomConstraints(updated);
  };

  const removeConstraint = (index: number) => {
    const updated = customConstraints.filter((_, i) => i !== index);
    setCustomConstraints(updated);
  };

  const steps = [
    {
      title: 'Type de validation',
      content: (
        <div style={{ padding: '24px 0' }}>
          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Card
                hoverable
                className={validationType === 'selected' ? 'selected-card' : ''}
                onClick={() => setValidationType('selected')}
                style={{ 
                  border: validationType === 'selected' ? '2px solid #1890ff' : '1px solid #d9d9d9',
                  marginBottom: 16
                }}
              >
                <Space>
                  <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                  <div>
                    <Title level={4}>Valider la sélection</Title>
                    <Text type="secondary">
                      Valider les {selectedCalculations.length} calculs sélectionnés
                    </Text>
                  </div>
                </Space>
              </Card>

              <Card
                hoverable
                className={validationType === 'conditional' ? 'selected-card' : ''}
                onClick={() => setValidationType('conditional')}
                style={{ 
                  border: validationType === 'conditional' ? '2px solid #1890ff' : '1px solid #d9d9d9'
                }}
              >
                <Space>
                  <FilterOutlined style={{ fontSize: 24, color: '#1890ff' }} />
                  <div>
                    <Title level={4}>Validation conditionnelle</Title>
                    <Text type="secondary">
                      Valider selon des critères spécifiques (département, salaire, etc.)
                    </Text>
                  </div>
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      )
    },
    {
      title: 'Contraintes',
      content: (
        <div style={{ padding: '24px 0' }}>
          <Title level={4}>Contraintes de validation</Title>
          <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
            Définissez les règles de validation à appliquer
          </Text>

          <div style={{ marginBottom: 16 }}>
            <Button type="dashed" onClick={addCustomConstraint} block>
              Ajouter une contrainte personnalisée
            </Button>
          </div>

          <List
            dataSource={customConstraints}
            renderItem={(constraint, index) => (
              <List.Item
                actions={[
                  <Button 
                    type="text" 
                    danger 
                    onClick={() => removeConstraint(index)}
                  >
                    Supprimer
                  </Button>
                ]}
              >
                <Row gutter={8} style={{ width: '100%' }}>
                  <Col span={6}>
                    <Select
                      value={constraint.field}
                      onChange={(value) => updateConstraint(index, 'field', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="net_salary">Salaire Net</Option>
                      <Option value="gross_salary">Salaire Brut</Option>
                      <Option value="cnss_employee">CNSS Employé</Option>
                      <Option value="iuts_amount">Montant IUTS</Option>
                    </Select>
                  </Col>
                  <Col span={4}>
                    <Select
                      value={constraint.operator}
                      onChange={(value) => updateConstraint(index, 'operator', value)}
                      style={{ width: '100%' }}
                    >
                      <Option value="min">Minimum</Option>
                      <Option value="max">Maximum</Option>
                      <Option value="equals">Égal à</Option>
                    </Select>
                  </Col>
                  <Col span={6}>
                    <InputNumber
                      value={constraint.value as number}
                      onChange={(value) => updateConstraint(index, 'value', value)}
                      style={{ width: '100%' }}
                      formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    />
                  </Col>
                  <Col span={8}>
                    <Text type="secondary">{constraint.message}</Text>
                  </Col>
                </Row>
              </List.Item>
            )}
          />
        </div>
      )
    }
  ];

  if (validationType === 'conditional') {
    steps.splice(1, 0, {
      title: 'Critères',
      content: (
        <div style={{ padding: '24px 0' }}>
          <Form form={form} layout="vertical">
            <Title level={4}>Critères de sélection</Title>
            <Text type="secondary" style={{ marginBottom: 16, display: 'block' }}>
              Définissez les critères pour sélectionner les calculs à valider
            </Text>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label="Salaire minimum">
                  <InputNumber
                    value={criteria.min_salary}
                    onChange={(value) => setCriteria({...criteria, min_salary: value || undefined})}
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    placeholder="Salaire minimum en FCFA"
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label="Salaire maximum">
                  <InputNumber
                    value={criteria.max_salary}
                    onChange={(value) => setCriteria({...criteria, max_salary: value || undefined})}
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                    placeholder="Salaire maximum en FCFA"
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <Form.Item label="Catégories professionnelles">
                  <Select
                    mode="multiple"
                    value={criteria.professional_categories}
                    onChange={(value) => setCriteria({...criteria, professional_categories: value})}
                    style={{ width: '100%' }}
                    placeholder="Sélectionner les catégories"
                  >
                    <Option value="cadre">Cadre</Option>
                    <Option value="non_cadre">Non-cadre</Option>
                    <Option value="agent_maitrise">Agent de maîtrise</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item>
                  <Checkbox
                    checked={criteria.exclude_validated}
                    onChange={(e) => setCriteria({...criteria, exclude_validated: e.target.checked})}
                  >
                    Exclure les calculs déjà validés
                  </Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      )
    });

    steps.push({
      title: 'Résultats',
      content: validationResult ? (
        <div style={{ padding: '24px 0' }}>
          <Title level={4}>Résultats de la validation</Title>
          
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Calculs traités"
                  value={validationResult.matched_calculations}
                  prefix={<TeamOutlined style={{ color: '#1890ff' }} />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Validés avec succès"
                  value={validationResult.summary.valid_calculations}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={8}>
              <Card>
                <Statistic
                  title="Avec erreurs"
                  value={validationResult.summary.invalid_calculations}
                  prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {validationResult.validation_results.length > 0 && (
            <div>
              <Title level={5}>Détails par employé</Title>
              <List
                size="small"
                dataSource={validationResult.validation_results.slice(0, 10)}
                renderItem={(result) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        result.is_valid ? 
                          <CheckCircleOutlined style={{ color: '#52c41a' }} /> :
                          <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                      }
                      title={result.employee_name}
                      description={
                        <Space>
                          {result.errors.length > 0 && (
                            <Tag color="error">{result.errors.length} erreur(s)</Tag>
                          )}
                          {result.warnings.length > 0 && (
                            <Tag color="warning">{result.warnings.length} avertissement(s)</Tag>
                          )}
                          {result.is_valid && result.errors.length === 0 && (
                            <Tag color="success">Validé</Tag>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
              {validationResult.validation_results.length > 10 && (
                <Text type="secondary">
                  ... et {validationResult.validation_results.length - 10} autres résultats
                </Text>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16 }}>
            Traitement en cours...
          </Text>
        </div>
      )
    });
  }

  const getFooter = () => {
    const isLastStep = currentStep === steps.length - 1;
    const isFirstStep = currentStep === 0;

    if (validationType === 'conditional' && isLastStep && validationResult) {
      return (
        <Space>
          <Button onClick={onCancel}>Fermer</Button>
          <Button type="primary" onClick={handleFinishConditional}>
            Terminer
          </Button>
        </Space>
      );
    }

    return (
      <Space>
        <Button onClick={onCancel}>Annuler</Button>
        {!isFirstStep && (
          <Button onClick={handlePrev}>Précédent</Button>
        )}
        {!isLastStep && (
          <Button type="primary" onClick={handleNext}>
            Suivant
          </Button>
        )}
        {isLastStep && validationType === 'selected' && (
          <Button 
            type="primary" 
            onClick={handleValidateSelected}
            loading={isProcessing}
            disabled={!constraintsReady}
            icon={<CheckOutlined />}
          >
            Valider la sélection
          </Button>
        )}
        {currentStep === steps.length - 2 && validationType === 'conditional' && (
          <Button 
            type="primary" 
            onClick={handleConditionalValidation}
            loading={isProcessing}
            disabled={!constraintsReady}
            icon={<FilterOutlined />}
          >
            Lancer la validation
          </Button>
        )}
      </Space>
    );
  };

  return (
    <Modal
      title="Validation en lot"
      open={visible}
      onCancel={onCancel}
      width={800}
      footer={getFooter()}
    >
      {constraintsError && <Alert type="error" showIcon message="Contraintes de validation indisponibles" description={constraintsError}
        action={<Button onClick={loadDefaultConstraints}>Réessayer</Button>} style={{ marginBottom: 16 }} />}
      <Steps current={currentStep} style={{ marginBottom: 24 }}>
        {steps.map((step, index) => (
          <Step key={index} title={step.title} />
        ))}
      </Steps>

      <div style={{ minHeight: 400 }}>
        {steps[currentStep]?.content}
      </div>

      {/* Alertes importantes */}
      {validationType === 'selected' && currentStep === 0 && (
        <Alert
          message={`${selectedCalculations.length} calcul(s) sélectionné(s)`}
          description="La validation sera appliquée à tous les calculs sélectionnés."
          type="info"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      {validationType === 'conditional' && currentStep === 1 && (
        <Alert
          message="Validation conditionnelle"
          description="Seuls les calculs répondant aux critères seront validés."
          type="warning"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}
    </Modal>
  );
};

export default ModalValidationLot;
